/**
 * Session API Types
 * Based on backend session API flow documentation
 */

// User relation bitmask (for determining if user has joined an adventure)
export enum UserRelationType {
  None = 0,
  Joined = 1,      // User has joined as participant
  Maintainer = 2,  // User is creator or maintainer
  Tester = 4,      // User is a tester
  Moderator = 8,   // User is a moderator
}

// Session status matching backend (AdventureSessionStatus enum)
export enum SessionStatus {
  NotStarted = 0,   // Session created but not yet started
  InProgress = 1,   // Session is actively in progress
  Paused = 2,       // Session is paused by user or system
  Completed = 3,    // Session completed successfully
  Failed = 4,       // Session ended in failure
  Abandoned = 5,    // Session was abandoned by user
}

// Node status within a session (matches backend SessionNodeStatus enum)
export enum SessionNodeStatus {
  Pending = 0,
  AwaitingInput = 1,
  AwaitingVote = 2,
  Processing = 3,
  Completed = 4,
  Waiting = 5,
  OnCooldown = 6,
}

// Session visibility for group sessions
export enum SessionVisibility {
  Private = 0,
  InviteOnly = 1,
  Public = 2,
}

// Participant role in a session
export enum SessionParticipantRole {
  Member = 0,
  Owner = 1,
}

// Invite status
export enum SessionInviteStatus {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
  Expired = 3,
}

// Boot vote status for kick voting
export enum BootVoteStatus {
  Active = 0,
  Passed = 1,
  Failed = 2,
  Expired = 3,
}

// Participant status in a session
export enum SessionParticipantStatus {
  Active = 0,
  Offline = 1,
  Left = 2,
  Removed = 3,
}

// Input types for challenge nodes
export type SessionInputType = 'QuestionAnswer' | 'Numpad' | 'NumericInput' | 'QRCode' | 'ModeratorApproval' | 'Codex'

// Journal entry types - matches backend JournalEntryType enum
export enum JournalEntryType {
  // System flow events (0-9)
  SessionStarted = 0,
  FormattedMessage = 1,
  TextClue = 2,
  Video = 3,
  Image = 4,
  AudioPlayed = 5,
  PoiRevealed = 6,
  AdventureUnlocked = 7,
  RouteRevealed = 8,

  // Challenge events (10-19)
  ChoiceMade = 10,
  AnswerSubmitted = 11,
  QRCodeScanned = 12,
  NumericInputEntered = 13,
  ModeratorApprovalRequested = 14,
  ModeratorApprovalReceived = 15,
  CodexInputEntered = 16,

  // Path events (20-29)
  PathTaken = 20,
  LoopDetected = 21,

  // Session events (30-39)
  SessionPaused = 30,
  SessionResumed = 31,
  SessionCompleted = 32,
  SessionFailed = 33,
  TimerPaused = 34,
  TimerResumed = 35,

  // Clue events (50-59)
  ClueGateUnlocked = 2,  // Alias for TextClue — clue gate unlocked clues into pool
  ClueRequested = 50,    // Player requested a clue from the pool

  // Group events (40-49)
  VotingStarted = 40,
  VoteCast = 41,
  VotingCompleted = 42,
  ParticipantJoined = 43,
  ParticipantLeft = 44,

  // User-generated entries (100+)
  UserNote = 100,
  UserImage = 101,
  UserMapMarker = 102,

  // Legacy aliases for backward compatibility
  PoiLocation = 6,         // Alias for PoiRevealed
  ImageDisplayed = 4,      // Alias for Image
  VideoPlayed = 3,         // Alias for Video
  ChallengePresented = 10, // Alias for ChoiceMade (challenge presentation)
  ChallengeAnswered = 11,  // Alias for AnswerSubmitted
  MapMarker = 102,         // Alias for UserMapMarker
}

// Session node data for display
export interface SessionNodeDto {
  id: string
  nodeType: number
  label: string
  description?: string
  // Content for auto-advance nodes
  formattedContent?: string        // FormattedMessage HTML
  textClue?: string                // TextClue message
  // POI location data
  poiLocation?: {
    latitude: number
    longitude: number
    description?: string
    markerEmoji?: string
    markerStyle?: 'icon' | 'circle'
    circleRadius?: number
    circleColor?: string
    circleBorderStyle?: 'solid' | 'dashed' | 'none'
    circleFillStyle?: 'solid' | 'gradient'
  }
  // Media content
  audioUrl?: string
  videoUrl?: string
  imageUrl?: string
  imageCaption?: string
  // Challenge node data
  questionText?: string
  answerType?: number              // QuestionAnswerType enum
  answerOptions?: Array<{
    id: string
    text: string
  }>
  digitCount?: number              // Numpad
  letterCount?: number             // Codex
  inputStyle?: number              // NumpadInputStyle enum
  instructions?: string            // ModeratorApproval
  // Button customization
  allowSkip?: boolean
  skipButtonText?: string
  cancelButtonText?: string
  // Exit node message
  exitMessage?: string
}

// Pending input requirements
export interface PendingInputDto {
  nodeId: string
  inputType: SessionInputType
  required: boolean
  allowSkip: boolean
  allowCancel: boolean
  skipButtonText?: string
  cancelButtonText?: string
}

// Challenge DTOs from API
export interface QuestionAnswerChallengeDto {
  questionText: string
  answerType: number
  answerOptions: Array<{ id: string; text: string }> | null
  allowSkip: boolean
  skipButtonText: string | null
  cancelButtonText: string | null
}

export interface NumpadChallengeDto {
  digitCount: number
  inputStyle: number
  allowSkip: boolean
  skipButtonText: string | null
  cancelButtonText: string | null
}

export interface QrCodeChallengeDto {
  instructions: string | null
  allowSkip: boolean
  skipButtonText: string | null
  cancelButtonText: string | null
}

export interface CodexChallengeDto {
  letterCount: number
  allowSkip: boolean
  skipButtonText: string | null
  cancelButtonText: string | null
}

export interface ModeratorApprovalChallengeDto {
  instructions: string | null
  allowSkip: boolean
  skipButtonText: string | null
  cancelButtonText: string | null
}

// Voting state for group sessions
export interface VotingStateDto {
  roundId: string
  nodeId: string
  status: 'Open' | 'Closed' | 'Resolved'
  options: VotingOptionDto[]
  userVote: string | null
  totalVoters: number
  votesCast: number
  requiredVotes: number
  timerExpiresAt: string | null
  percentageVoted: number
  isComplete: boolean
}

export interface VotingOptionDto {
  id: string
  label: string
  voteCount?: number  // Only visible based on votingVisibility setting
}

// Full session state
export interface SessionStateDto {
  sessionId: string
  adventureId: string
  adventureTitle: string
  status: SessionStatus
  currentNodeId: string | null
  currentNode: SessionNodeDto | null
  currentNodeStatus: SessionNodeStatus
  currentNodeStatusName?: string
  // Cooldown state (when currentNodeStatus === OnCooldown)
  cooldownEndsAt: string | null
  waitingReason: string | null
  requiresInput: boolean
  inputType?: SessionInputType
  pendingInput: PendingInputDto | null
  // Challenge data (only one will be set based on inputType)
  questionAnswerChallenge: QuestionAnswerChallengeDto | null
  numpadChallenge: NumpadChallengeDto | null
  qrCodeChallenge: QrCodeChallengeDto | null
  codexChallenge: CodexChallengeDto | null
  moderatorApprovalChallenge: ModeratorApprovalChallengeDto | null
  // Progress
  progressPercent: number
  startedAt: string | null
  completedAt: string | null
  lastActivityAt: string | null
  elapsedSeconds: number
  // Group session
  isSolo: boolean
  isGroup: boolean
  groupId: string | null
  participantCount: number
  // Voting
  isVotingActive: boolean
  activeVotingRound: VotingStateDto | null
  // Test session
  isTestSession?: boolean  // True if this is a test session using the Testing workflow version
  // Clue state
  hasAvailableClues?: boolean  // True if session has unlocked clues available to request
  // Timer control
  effectiveDuration?: string          // HH:MM:SS effective time (the adventure clock value)
  totalPausedSeconds: number          // Total seconds the timer has been paused
  isTimerPaused: boolean              // Whether the adventure clock is currently paused
  timeModifierTotalSeconds?: number   // Total time modifier in seconds (bonuses/penalties from TimeModifier nodes)
  timePenaltyFromCluesSeconds?: number // Total time penalty from clue usage
  carriedOverPenaltySeconds?: number  // Penalty seconds carried over from previous sessions
  completionSummary?: CompletionSummaryDto | null
  // Group session - journal & proximity
  latestJournalSequenceNumber: number
  lastJournalEntryAt: string | null
  conversationId: string | null
  proximityRequirementMet: boolean | null
  proximityWarning: string | null
  // Voting settings (embedded in session responses for group sessions)
  votingSettings: SessionVotingSettings | null
}

// Session list item (for checking existing sessions)
export interface SessionListDto {
  id: string
  adventureId: string
  adventureTitle: string
  status: SessionStatus
  startedAt: string | null
  completedAt: string | null
  progressPercent: number
  isSolo: boolean
  isGroup: boolean
  groupId: string | null
  isTestSession?: boolean  // True if this is a test session using the Testing workflow version
}

// Journal entry
export interface JournalEntryDto {
  id: string
  sessionId: string
  sequenceNumber: number
  entryType: JournalEntryType
  entryTypeName: string
  content: Record<string, unknown> | null
  createdAt: string
  isSystemGenerated: boolean
  // Parsed content fields for common types
  htmlContent?: string         // FormattedMessage
  textContent?: string         // TextClue, UserNote
  choice?: string              // ChoiceMade
  result?: string              // ChoiceMade (correct/incorrect)
  branchKey?: string           // PathTaken
  latitude?: number            // MapMarker
  longitude?: number           // MapMarker
  markerEmoji?: string         // MapMarker
  note?: string                // MapMarker, UserNote
  clueTitle?: string           // ClueRequested
  clueHtmlContent?: string     // ClueRequested
  adventureClockSeconds?: number  // Adventure clock time at this entry (excluding paused time)
}

// Reward granted during session
export interface SessionRewardDto {
  rewardType: string
  nodeId: string
  nodeLabel: string
  content: Record<string, unknown>
  grantedAt: string
}

// Completion summary DTOs (returned when session reaches terminal state)
export interface TimeBreakdownDto {
  wallDuration: string
  effectiveDuration: string
  pausedSeconds: number
  timeModifierSeconds: number
  cluePenaltySeconds: number
  carriedOverPenaltySeconds: number
  cluesUsed: number
}

export interface LeaderboardSummaryDto {
  position: number
  totalPlayers: number
  bestTimeFormatted: string
}

export interface AchievementProgressDto {
  achievementId: string
  achievementName: string
  description: string
  iconUrl: string | null
  completedCount: number
  totalAdventureCount: number
  completionPercentage: number
  isCompleted: boolean
  hasBlockingFailures: boolean
  completedDate: string | null
}

export interface CollectionProgressDto {
  collectionId: string
  collectionName: string
  completedAchievementCount: number
  totalAchievementCount: number
  completionPercentage: number
  isCompleted: boolean
}

export interface CompletionSummaryDto {
  finalStatus: string
  timeBreakdown: TimeBreakdownDto
  leaderboard: LeaderboardSummaryDto | null
  achievements: AchievementProgressDto[]
  collections: CollectionProgressDto[]
}

// Process response
export interface ProcessResponseDto {
  success: boolean
  session: SessionStateDto
  newNode?: SessionNodeDto
  edgeTaken?: string
  newJournalEntries: JournalEntryDto[]
  newRewards: SessionRewardDto[]
  message?: string
  completionSummary?: CompletionSummaryDto | null
}

// Input response
export interface InputResponseDto {
  success: boolean
  session: SessionStateDto
  edgeTaken?: string
  isCorrect?: boolean
  newJournalEntries: JournalEntryDto[]
  newRewards: SessionRewardDto[]
  message?: string
  completionSummary?: CompletionSummaryDto | null
}

// Abandon response
export interface AbandonResponseDto {
  completionSummary?: CompletionSummaryDto | null
}

// Session participant info (for group play UI)
// Matches SessionParticipantDto from GET /api/sessions/{id}/participants
export interface SessionParticipantInfo {
  id: string
  userId: string
  userName: string
  avatarUrl?: string
  status: SessionParticipantStatus
  role: SessionParticipantRole
  isOnline: boolean
  joinedAt: string
  lastSeenAt?: string
  isReady: boolean
  readyAt: string | null
  lastViewedJournalSequenceNumber: number | null
}

// Public session listing for browsing
export interface PublicSessionCreatorDto {
  entityId: string
  entityTypeId: number
  entityTypeName: string
  entityName: string
  entityAvatarUrl: string | null
}

export interface PublicQueueSessionDto {
  sessionId: string
  sessionName: string
  adventureId: string
  adventureName: string
  startDateTime?: string
  memberCount: number
  maxParticipants: number
  creator: PublicSessionCreatorDto
  genderTags: string[]
  interestTags: string[]
  lookingForTags: string[]
  createdDate: string
}

// Session invite
export interface SessionInviteDto {
  id: string
  userId: string
  userDisplayName?: string
  userAvatarUrl?: string | null
  invitedBy: string
  invitedByDisplayName?: string
  invitedByAvatarUrl?: string | null
  invitedDate: string
  expirationDate?: string | null
  status: string // "Pending" | "Accepted" | "Declined" | "Expired"
  isExpired: boolean
}

// Request to create an invite
export interface CreateSessionInviteRequest {
  userId: string
}

// Share code response
export interface ShareCodeResponse {
  shareCode: string
}

// Voting settings for group sessions (SessionVotingSettingsDto from backend)
// Includes canEdit* flags indicating which settings the session owner can change
export interface SessionVotingSettings {
  votingVisibility: number
  voteChangePolicy: number
  voteChangeWindowSeconds: number | null
  votingCompletionMode: number
  votingCompletionPercentage: number | null
  votingCompletionTimerSeconds: number | null
  votingTieResolution: number
  votingTieTimeoutSeconds: number | null
  offlineMemberAction: number
  maxParticipantDistanceFeet: number | null
  participantProximityPercentage: number | null
  canEditVotingVisibility: boolean
  canEditVoteChangePolicy: boolean
  canEditVotingCompletionMode: boolean
  canEditVotingTieResolution: boolean
  canEditOfflineMemberAction: boolean
}

// Full session DTO (detailed session info including participants)
// Returned by POST /api/sessions for group session creation (201 Created, not wrapped in ApiResponse)
export interface AdventureSessionFullDto {
  id: string
  adventureId: string
  adventureTitle: string
  userId: string | null
  creatorId: string
  sessionName?: string
  visibility: SessionVisibility
  maxParticipants: number | null
  currentNodeId: string | null
  currentNode: SessionNodeDto | null
  status: SessionStatus
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  duration: string | null
  effectiveDuration: string | null
  totalPausedSeconds: number
  isTimerPaused: boolean
  timeModifierTotalSeconds: number
  timePenaltyFromCluesSeconds: number
  carriedOverPenaltySeconds: number
  participants: SessionParticipantInfo[]
  votingSettings: SessionVotingSettings | null
  isSolo: boolean
  isGroup: boolean
  requiresInput: boolean
  inputType: SessionInputType | null
  isVotingActive: boolean
  activeVotingRound: VotingStateDto | null
  isTestSession: boolean
  isAnonymous: boolean
  conversationId: string | null
  allParticipantsReady: boolean
}

// Request types
export interface CreateSessionRequest {
  adventureId: string
  groupId?: string | null
  useTestVersion?: boolean  // If true, creates a test session using the Testing workflow version
  sessionName?: string
  visibility?: SessionVisibility
  maxParticipants?: number
  tags?: string[]
  requireTagMatch?: boolean
  isAnonymous?: boolean | null
  genderTagsJson?: string | null
  interestTagsJson?: string | null
  lookingForTagsJson?: string | null
  startDateTime?: string | null
}

export interface SubmitInputRequest {
  nodeId: string
  inputType: SessionInputType
  value: string
}

export interface AddJournalNoteRequest {
  note: string
}

export interface AddMapMarkerRequest {
  latitude: number
  longitude: number
  note?: string
  markerEmoji?: string
}

export interface CastVoteRequest {
  votingRoundId: string
  voteValue: string
}

// Create session response
export interface CreateSessionResponseDto {
  id: string
  adventureId: string
  adventureTitle: string
  status: SessionStatus
  currentNodeId: string
  currentNode: SessionNodeDto
  isSolo: boolean
  isGroup: boolean
  requiresInput: boolean
  createdAt: string
  isTestSession?: boolean  // True if this is a test session using the Testing workflow version
}

// Vote response (individual vote record)
export interface CastVoteResponseDto {
  id: string
  userId: string
  voteValue: string
  castAt: string
  hasChanged: boolean
}

// Voting resolution result (returned when a round is resolved)
export interface VotingResolutionResultDto {
  isResolved: boolean
  winningValue: string | null
  winningLabel: string | null
  method: string | null  // e.g. "Majority", "Random", "Revote"
  roundId: string
  resolvedAt: string | null
}

// Session transition result (returned when session advances after vote resolution)
export interface SessionTransitionResultDto {
  success: boolean
  session: SessionStateDto
  newNode: SessionNodeDto | null
  edgeTaken: string | null
  newJournalEntries: JournalEntryDto[]
  newRewards: SessionRewardDto[]
  message: string | null
}

// Composite result from castVote / forceResolve endpoints
export interface CastVoteResultDto {
  vote: CastVoteResponseDto | null
  resolution: VotingResolutionResultDto | null
  transitionResult: SessionTransitionResultDto | null
}

// Voting progress response
export interface VotingProgressDto {
  roundId: string
  totalParticipants: number
  votesCast: number
  votesPending: number
  percentageVoted: number
  pendingParticipantIds: string[]
  isComplete: boolean
  timerExpiresAt: string | null
}

// ========================
// Debug DTOs (for maintainers/owners)
// ========================

// Summary of a session for debugging (list view)
export interface SessionDebugSummaryDto {
  id: string
  adventureId: string
  userId: string
  userName?: string
  groupId?: string | null
  groupName?: string | null
  status: SessionStatus
  statusName: string
  isTestSession: boolean
  startedAt: string | null
  completedAt: string | null
  lastActivityAt: string | null
  progressPercent: number
  nodeVisitCount: number
  participantCount: number
}

// Participant in a session
export interface SessionParticipantDto {
  id: string
  userId: string
  userName?: string
  avatarUrl?: string
  status: string
  joinedAt: string
  lastActivityAt: string | null
  isActive: boolean
  isReady: boolean
  readyAt: string | null
  lastViewedJournalSequenceNumber: number | null
}

// Node visit history entry
export interface NodeVisitDto {
  id: string
  nodeId: string
  nodeLabel?: string
  nodeType: number
  nodeTypeName: string
  enteredAt: string
  exitedAt: string | null
  exitBranch?: string | null
  durationSeconds?: number | null
}

// Detailed session data for debugging
export interface SessionDebugDetailDto {
  id: string
  adventureId: string
  adventureTitle: string
  workflowVersionId: string
  workflowVersionLabel?: string
  status: SessionStatus
  statusName: string
  isTestSession: boolean
  isSolo: boolean
  isGroup: boolean
  groupId: string | null
  groupName?: string | null
  startedAt: string | null
  completedAt: string | null
  lastActivityAt: string | null
  progressPercent: number
  currentNodeId: string | null
  // Participants
  participants: SessionParticipantDto[]
  // Node visit history
  nodeVisits: NodeVisitDto[]
  // Journal entries
  journalEntries: JournalEntryDto[]
  // Rewards granted
  rewards: SessionRewardDto[]
}

// ========================
// Boot Vote / Ready / Journal DTOs
// ========================

export interface InitiateBootVoteRequest {
  targetUserId: string
  reason?: string
}

export interface CastBootVoteRequest {
  voteFor: boolean
}

export interface MarkJournalReadRequest {
  sequenceNumber: number
}

export interface SessionBootVoteDto {
  id: string
  sessionId: string
  targetUserId: string
  targetUserName: string
  initiatorUserId: string
  initiatorUserName: string
  status: BootVoteStatus
  reason?: string
  votesFor: number
  votesAgainst: number
  requiredVotes: number
  createdAt: string
  expiresAt: string | null
}

export interface ModeratorStatusDto {
  adventureId: string
  hasOnlineModerator: boolean
  totalModerators: number
  onlineModerators: number
  lastModeratorActiveAt: string | null
}

export interface SessionStartValidationDto {
  canStart: boolean
  errors: string[]
  allParticipantsReady: boolean
  proximityRequirementMet: boolean
  activeParticipantCount: number
  minimumParticipants: number
}

// ========================
// Session Settings Enums & DTOs
// ========================

export enum VotingVisibility {
  GroupDefined = 0,
  HiddenUntilCompleted = 1,
  RealTime = 2,
}

export enum VoteChangePolicy {
  GroupDefined = 0,
  LockedAfterSubmission = 1,
  LimitedWindow = 2,
  AllowedUntilClose = 3,
}

export enum VotingCompletionMode {
  GroupDefined = 0,
  VotingPercentage = 1,
  TimerBased = 2,
}

export enum VotingTieResolution {
  GroupDefined = 0,
  TimeoutRandomSelection = 1,
  RevoteOnTiedOptions = 2,
}

export enum OfflineMemberAction {
  GroupDefined = 0,
  Pause = 1,
  Continue = 2,
  Abandon = 3,
  VoteOnAction = 4,
}

export interface UpdateSessionSettingsRequest {
  votingVisibility?: number
  voteChangePolicy?: number
  voteChangeWindowSeconds?: number | null
  votingCompletionMode?: number
  votingCompletionPercentage?: number | null
  votingCompletionTimerSeconds?: number | null
  votingTieResolution?: number
  votingTieTimeoutSeconds?: number | null
  offlineMemberAction?: number
}

// Helper function to check if user has joined an adventure
export function hasJoinedAdventure(userRelationType: number | undefined): boolean {
  if (userRelationType === undefined || userRelationType === null) return false
  return (
    (userRelationType & UserRelationType.Joined) !== 0 ||
    (userRelationType & UserRelationType.Tester) !== 0
  )
}

// Helper function to check specific relation
export function hasRelation(
  userRelationType: number | undefined,
  relation: UserRelationType
): boolean {
  if (userRelationType === undefined || userRelationType === null) return false
  return (userRelationType & relation) !== 0
}
